import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type {
  Assistant,
  AssistantType,
  Integration,
  AssistantFunction,
  AssistantModel,
} from '../../types/assistant';
import type { SurveyQuestionnaire } from '../../types/survey';
import type { TimeCondition } from '../../types/timeCondition';
import { Form, FormField, FormLabel } from '../ui';
import { FormInput, FormSelect, FormTextarea } from '../ui/FormControls';
import { FormCheckbox } from '../ui/FormCheckbox';
import Button from '../ui/Button';

const assistantSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  assistant_model_id: z.string().min(1, 'Modelo é obrigatório'),
  type: z.string().min(1, 'Tipo é obrigatório'),
  instructions: z.string().min(1, 'Instruções são obrigatórias'),
  summary: z.string().optional(),
  signature: z.string().min(1, 'Assinatura é obrigatória'),
  integration_id: z.string().optional(),
  time_condition_id: z.string().optional(),
  status_mode: z.enum(['always_active', 'always_inactive', 'auto', 'triggered']),
  trigger_keywords: z.string().optional(),
  trigger_duration: z.string().optional(),
  trigger_models: z.string().optional(),
  voice_id: z.string().optional(),
  generate_resume: z.boolean().optional(),
  close_ticket_at: z.string().optional(),
  send_messages_only_ticketopen: z.boolean().optional(),
  generate_resume_alerts: z.boolean().optional(),
  respond_on_first_group_message: z.boolean().optional(),
  close_survey_id: z.string().optional(),
  contact_name_update: z.boolean().optional(),
});

type FormData = z.infer<typeof assistantSchema>;

interface AssistantFormProps {
  assistant: Assistant | null;
  assistantTypes: AssistantType[];
  assistantModels: AssistantModel[];
  integrations?: Integration[];
  timeConditions?: TimeCondition[];
  assistantFunctions?: AssistantFunction[];
  allAssistants?: Assistant[];
  surveys?: SurveyQuestionnaire[];
  selectedRelatedAssistants: number[];
  selectedRelatedFunctions: number[];
  onRelatedAssistantsChange: (assistants: number[]) => void;
  onRelatedFunctionsChange: (functions: number[]) => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  onClose: () => void;
}

function AssistantForm({
  assistant,
  assistantTypes,
  assistantModels,
  integrations = [],
  timeConditions = [],
  assistantFunctions = [],
  allAssistants = [],
  surveys = [],
  selectedRelatedAssistants,
  selectedRelatedFunctions,
  onRelatedAssistantsChange,
  onRelatedFunctionsChange,
  onSubmit,
  isLoading,
  onClose,
}: AssistantFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(assistantSchema),
    defaultValues: {
      name: assistant?.name || '',
      assistant_model_id: assistant?.assistant_model_id?.toString() || '',
      type: assistant?.type || '',
      instructions: assistant?.instructions || '',
      summary: assistant?.summary || '',
      signature: assistant?.signature || '',
      integration_id: assistant?.integration_id?.toString() || '',
      time_condition_id: assistant?.time_condition_id?.toString() || '',
      status_mode: assistant?.status_mode || 'auto',
      trigger_keywords: assistant?.trigger_keywords?.join(', ') || '',
      trigger_duration: assistant?.trigger_duration?.toString() || '',
      trigger_models: assistant?.trigger_models?.join('\n') || '',
      voice_id: assistant?.voice_id || '',
      generate_resume: assistant?.generate_resume || false,
      close_ticket_at: assistant?.close_ticket_at?.toString() || '',
      send_messages_only_ticketopen: assistant?.send_messages_only_ticketopen || false,
      generate_resume_alerts: assistant?.generate_resume_alerts || false,
      respond_on_first_group_message: assistant?.respond_on_first_group_message || false,
      close_survey_id: assistant?.close_survey_id?.toString() || '',
      contact_name_update: assistant?.contact_name_update !== false,
    },
  });

  const [activeTab, setActiveTab] = useState('general');

  // Group functions by their group property
  const functionGroups =
    assistantFunctions?.reduce((groups, func) => {
      const group = func.group || 'Outros';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(func);
      return groups;
    }, {} as Record<string, AssistantFunction[]>) || {};

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Geral
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('triggers')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'triggers'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Gatilhos
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('assistants')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assistants'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Assistentes Relacionados
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('functions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'functions'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Funções Relacionadas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Configurações
          </button>
        </nav>
      </div>

      {activeTab === 'general' && (
        <div className="space-y-4 pt-4">
          {!assistant && (
            <FormField>
              <FormLabel htmlFor="name" required>
                Nome
              </FormLabel>
              <FormInput
                {...register('name')}
                error={!!errors.name}
                validationError={errors.name?.message}
              />
            </FormField>
          )}

          <FormField>
            <FormLabel htmlFor="assistant_model_id" required>
              Modelo
            </FormLabel>
            <FormSelect
              {...register('assistant_model_id')}
              error={!!errors.assistant_model_id}
              validationError={errors.assistant_model_id?.message}
            >
              <option value="">Selecione um modelo</option>
              {assistantModels
                ?.filter(model => model.is_active)
                .map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name} - {model.provider}
                  </option>
                ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="type" required>
              Tipo
            </FormLabel>
            <FormSelect
              {...register('type')}
              error={!!errors.type}
              validationError={errors.type?.message}
            >
              <option value="">Selecione um tipo</option>
              {assistantTypes.map(type => (
                <option key={type.type} value={type.type}>
                  {type.description}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <FormLabel htmlFor="instructions" required>
              Instruções
            </FormLabel>
            <FormTextarea
              {...register('instructions')}
              error={!!errors.instructions}
              validationError={errors.instructions?.message}
              rows={10}
              className="font-mono text-sm"
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="summary">Resumo</FormLabel>
            <div className="mb-1 text-xs text-gray-500">
              Se deixar vazio, o sistema irá gerar automaticamente
            </div>
            <FormTextarea
              {...register('summary')}
              error={!!errors.summary}
              validationError={errors.summary?.message}
              rows={2}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="signature" required>
              Assinatura
            </FormLabel>
            <FormInput
              {...register('signature')}
              error={!!errors.signature}
              validationError={errors.signature?.message}
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField>
              <FormLabel htmlFor="time_condition_id">Condição de Tempo</FormLabel>
              <FormSelect
                {...register('time_condition_id')}
                error={!!errors.time_condition_id}
                validationError={errors.time_condition_id?.message}
              >
                <option value="">Selecione uma condição (opcional)</option>
                {timeConditions?.map(condition => (
                  <option key={condition.id} value={condition.id}>
                    {condition.description}
                  </option>
                ))}
              </FormSelect>
            </FormField>

            <FormField>
              <FormLabel htmlFor="status_mode" required>
                Modo de Status
              </FormLabel>
              <FormSelect
                {...register('status_mode')}
                error={!!errors.status_mode}
                validationError={errors.status_mode?.message}
              >
                <option value="auto">Automático (baseado nas condições de tempo)</option>
                <option value="always_active">Sempre Ativo</option>
                <option value="always_inactive">Sempre Inativo</option>
                <option value="triggered">Ativado por Gatilho</option>
              </FormSelect>
            </FormField>

            {assistant?.current_status && (
              <FormField>
                <FormLabel htmlFor="current_status">Status Atual</FormLabel>
                <div className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded-md">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      assistant.current_status === 'active'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}
                  >
                    {assistant.current_status === 'active' ? 'Ativo' : 'Inativo'}
                  </span>
                  <p className="mt-1 text-xs text-gray-500">
                    Este campo é gerenciado pelo sistema e não pode ser modificado diretamente.
                  </p>
                </div>
              </FormField>
            )}
          </div>

          <FormField>
            <FormLabel htmlFor="integration_id">Integração (opcional)</FormLabel>
            <FormSelect
              {...register('integration_id')}
              error={!!errors.integration_id}
              validationError={errors.integration_id?.message}
            >
              <option value="">Selecione uma integração (opcional)</option>
              {integrations.map(integration => (
                <option key={integration.id} value={integration.id}>
                  {integration.description}
                </option>
              ))}
            </FormSelect>
          </FormField>
        </div>
      )}

      {activeTab === 'triggers' && (
        <div className="space-y-4 pt-4">
          <FormField>
            <FormLabel htmlFor="trigger_keywords">Palavras-chave</FormLabel>
            <div className="mb-1 text-xs text-gray-500">
              Separe múltiplas palavras-chave com vírgulas
            </div>
            <FormInput
              {...register('trigger_keywords')}
              error={!!errors.trigger_keywords}
              validationError={errors.trigger_keywords?.message}
              placeholder="palavra1, palavra2, palavra3"
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="trigger_models">Mensagem Modelo</FormLabel>
            <FormTextarea
              {...register('trigger_models')}
              error={!!errors.trigger_models}
              validationError={errors.trigger_models?.message}
              rows={6}
              placeholder="Digite a mensagem modelo..."
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="trigger_duration">Timeout (minutos)</FormLabel>
            <FormInput
              type="number"
              {...register('trigger_duration')}
              error={!!errors.trigger_duration}
              validationError={errors.trigger_duration?.message}
              placeholder="0"
            />
          </FormField>
        </div>
      )}

      {activeTab === 'assistants' && (
        <div className="space-y-4 pt-4">
          <FormField>
            <FormLabel>Assistentes Relacionados</FormLabel>
            <div className="border rounded-md p-4 space-y-2 max-h-96 overflow-y-auto">
              {allAssistants
                .filter(a => a.id !== assistant?.id)
                .map(relatedAssistant => (
                  <div key={relatedAssistant.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`related-assistant-${relatedAssistant.id}`}
                      checked={selectedRelatedAssistants.includes(relatedAssistant.id)}
                      onChange={e => {
                        if (e.target.checked) {
                          onRelatedAssistantsChange([
                            ...selectedRelatedAssistants,
                            relatedAssistant.id,
                          ]);
                        } else {
                          onRelatedAssistantsChange(
                            selectedRelatedAssistants.filter(id => id !== relatedAssistant.id)
                          );
                        }
                      }}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`related-assistant-${relatedAssistant.id}`}
                      className="ml-2 block text-sm text-gray-900"
                    >
                      {relatedAssistant.name} ({relatedAssistant.type})
                    </label>
                  </div>
                ))}
              {allAssistants.filter(a => a.id !== assistant?.id).length === 0 && (
                <p className="text-sm text-gray-500">Nenhum outro assistente disponível</p>
              )}
            </div>
          </FormField>
        </div>
      )}

      {activeTab === 'functions' && (
        <div className="space-y-4 pt-4">
          <FormField>
            <FormLabel>Funções Relacionadas</FormLabel>
            <div className="border rounded-md p-4 space-y-6 max-h-96 overflow-y-auto">
              {Object.entries(functionGroups).map(([groupName, functions]) => (
                <div key={groupName} className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 text-sm border-b pb-1">
                    {groupName}
                  </h3>
                  <div className="space-y-2 pl-2">
                    {functions.map(func => (
                      <div key={func.id} className="flex items-start">
                        <input
                          type="checkbox"
                          id={`related-function-${func.id}`}
                          checked={selectedRelatedFunctions.includes(func.id)}
                          onChange={e => {
                            if (e.target.checked) {
                              onRelatedFunctionsChange([...selectedRelatedFunctions, func.id]);
                            } else {
                              onRelatedFunctionsChange(
                                selectedRelatedFunctions.filter(id => id !== func.id)
                              );
                            }
                          }}
                          className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="ml-2 flex-1">
                          <label
                            htmlFor={`related-function-${func.id}`}
                            className="block text-sm font-medium text-gray-900"
                          >
                            {func.name}
                          </label>
                          <div className="flex flex-col">
                            <p className="text-xs text-gray-500 mt-1">{func.description}</p>
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 mt-1 rounded font-mono">
                              {func.function}
                            </code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {assistantFunctions.length === 0 && (
                <p className="text-sm text-gray-500">Nenhuma função disponível</p>
              )}
            </div>
          </FormField>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField>
              <FormLabel htmlFor="voice_id">ID de Voz</FormLabel>
              <div className="mb-1 text-xs text-gray-500">
                ID para funcionalidade de text-to-speech
              </div>
              <FormInput
                {...register('voice_id')}
                error={!!errors.voice_id}
                validationError={errors.voice_id?.message}
                placeholder="ex: pt-BR-FranciscaNeural"
                maxLength={200}
              />
            </FormField>

            <FormField>
              <FormLabel htmlFor="close_ticket_at">Fechar Ticket Após (minutos)</FormLabel>
              <div className="mb-1 text-xs text-gray-500">
                Tempo de inatividade para fechar automaticamente
              </div>
              <FormInput
                type="number"
                {...register('close_ticket_at')}
                error={!!errors.close_ticket_at}
                validationError={errors.close_ticket_at?.message}
                placeholder="0"
                min="0"
              />
            </FormField>
          </div>

          <FormField>
            <FormLabel htmlFor="close_survey_id">Questionário ao Fechar Ticket</FormLabel>
            <div className="mb-1 text-xs text-gray-500">
              Questionário enviado ao cliente quando o ticket é fechado
            </div>
            <FormSelect
              {...register('close_survey_id')}
              error={!!errors.close_survey_id}
              validationError={errors.close_survey_id?.message}
            >
              <option value="">Nenhum (opcional)</option>
              {surveys.map(survey => (
                <option key={survey.id} value={survey.id}>
                  {survey.title}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox {...register('generate_resume')} id="generate_resume" />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="generate_resume" className="font-medium">
                  Gerar Resumo de Conversação
                </FormLabel>
                <p className="text-gray-500">
                  Gera automaticamente um resumo ao final de cada conversação
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  {...register('send_messages_only_ticketopen')}
                  id="send_messages_only_ticketopen"
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="send_messages_only_ticketopen" className="font-medium">
                  Enviar Mensagens Apenas com Ticket Aberto
                </FormLabel>
                <p className="text-gray-500">
                  Restringe o envio de mensagens apenas quando o ticket está aberto
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox {...register('generate_resume_alerts')} id="generate_resume_alerts" />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="generate_resume_alerts" className="font-medium">
                  Gerar Resumo dos Alertas de Rede
                </FormLabel>
                <p className="text-gray-500">
                  Gera alertas automáticos quando um resumo de conversação é criado
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  {...register('respond_on_first_group_message')}
                  id="respond_on_first_group_message"
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="respond_on_first_group_message" className="font-medium">
                  Responder na Primeira Mensagem do Grupo
                </FormLabel>
                <p className="text-gray-500">
                  Responde automaticamente à primeira mensagem enviada em um grupo
                </p>
              </div>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <FormCheckbox
                  {...register('contact_name_update')}
                  id="contact_name_update"
                />
              </div>
              <div className="ml-3 text-sm">
                <FormLabel htmlFor="contact_name_update" className="font-medium">
                  Atualizar Nome do Contato Automaticamente
                </FormLabel>
                <p className="text-gray-500">
                  Atualiza o nome do contato com base no conteúdo da conversa
                </p>
              </div>
            </div>
          </FormField>
        </div>
      )}

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {assistant ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default AssistantForm;
